/**
 * FREIO DA CASCATA DE PULOS (2026-10-21).
 *
 * <p>Quando um item não carrega (imagem remota com erro, vídeo que não abre, URL que não é
 * mídia), o player pula pro seguinte — certo pra uma falha ISOLADA. Mas quando TODOS falham
 * (mídia http bloqueada no Embedded, rede instável logo após trocar de programação, URLs
 * vencidas), cada pulo leva 5-20 ms e o player rodava a playlist inteira ~13 vezes em 0,7 s
 * (medido no Embedded da bancada, 2026-10-21). Na tela isso aparece como "erro".</p>
 *
 * <p>Regra: o freio engata quando cada conteúdo DISTINTO da playlist já falhou sem nenhum
 * item tocar no meio — uma volta inteira. A partir daí o player SEGURA a tela atual e só
 * tenta de novo depois de um backoff (1 s, 2 s, 4 s, 8 s, teto 10 s). Enquanto segura, o
 * `usePlayableUrl` continua re-resolvendo: se o download do item terminar, ele carrega do
 * cache sozinho e toca. O primeiro item que toca de verdade zera tudo.</p>
 *
 * <p>Conta conteúdos distintos (e não slots) porque a playlist do Tier 2/3 é a sequência da
 * HORA inteira — o mesmo padrão repetido N vezes —, e porque um conteúdo repetido em slots
 * seguidos não pode, sozinho, engatar o freio: só uma falha de TODOS engata.</p>
 */

/** Espera antes de cada nova tentativa, em ordem; a última se repete (teto). */
export const FAILURE_BRAKE_BACKOFF_MS = [1_000, 2_000, 4_000, 8_000, 10_000];

export type FailureDecision =
  | { action: 'skip' }
  | { action: 'hold'; delayMs: number; attempt: number };

export class FailureBrake {
  private failedIds = new Set<string>();
  private holds = 0;
  private playlistVersion: number | null = null;

  /**
   * Registra a falha do item atual e diz o que fazer: pular (falha isolada, comportamento de
   * sempre) ou segurar (a volta inteira falhou).
   */
  recordFailure(p: {
    mediaId: string;
    playlistIds: string[];
    playlistVersion: number;
  }): FailureDecision {
    // Programação nova = itens novos: falhas da anterior não contam.
    if (this.playlistVersion !== p.playlistVersion) {
      this.reset();
      this.playlistVersion = p.playlistVersion;
    }
    this.failedIds.add(p.mediaId);

    const distinct = new Set(p.playlistIds).size;
    if (distinct === 0 || this.failedIds.size < distinct) {
      return { action: 'skip' };
    }

    const delayMs =
      FAILURE_BRAKE_BACKOFF_MS[
        Math.min(this.holds, FAILURE_BRAKE_BACKOFF_MS.length - 1)
      ];
    this.holds++;
    return { action: 'hold', delayMs, attempt: this.holds };
  }

  /** Um item tocou de verdade: a cascata acabou. */
  recordSuccess(): void {
    this.reset();
  }

  get isEngaged(): boolean {
    return this.holds > 0;
  }

  reset(): void {
    this.failedIds.clear();
    this.holds = 0;
    this.playlistVersion = null;
  }
}
